import React, { useEffect, useState } from 'react';
import {
    Box,
    Card,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Avatar,
    Chip,
    IconButton,
    Tooltip,
    CircularProgress,
    Tabs,
    Tab,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Stack,
    TablePagination
} from '@mui/material';
import GridViewIcon from '@mui/icons-material/GridView';
import MapIcon from '@mui/icons-material/Map';
import InfoIcon from '@mui/icons-material/Info';
import PndApi from 'src/utils/pnd/PndRestApi';
import { useHistory } from 'react-router-dom';
import RiderLocationMapModal from 'src/containers/PnD/RiderLocationHistory/View/components/RiderLocationMapModal';
import DashboardLiveMap from './DashboardLiveMap';

const LiveMapRiderGrid = ({ selectedHubId }) => {
    const [riders, setRiders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'map'
    const [statusFilter, setStatusFilter] = useState('all');
    const [riderFilter, setRiderFilter] = useState('all');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const history = useHistory();

    // modal states
    const [mapModalShow, setMapModalShow] = useState(false);
    const [mapData, setMapData] = useState(null);

    useEffect(() => {
        fetchRiders(selectedHubId);
        setStatusFilter('all');
        setRiderFilter('all');
        const interval = setInterval(() => fetchRiders(selectedHubId), 60000); // refresh every minute
        return () => clearInterval(interval);
    }, [selectedHubId]);

    const fetchRiders = async (hubId) => {
        try {
            setLoading(true);
            const response = await PndApi.FindRiderLiveGridViewData(hubId);
            if (response.status === 200 && response.data.status === 200) {
                // Ensure array shape if payload object is differently formatted
                const payload = response.data.data;
                if (Array.isArray(payload)) setRiders(payload);
                else if (payload && Array.isArray(payload.tableRowsData)) setRiders(payload.tableRowsData);
                else setRiders([]);
            } else if (response.status === 200 && response.data.status === 204) {
                setRiders([]);
            } else {
                setRiders([]);
            }
        } catch (error) {
            console.error('Error fetching riders:', error);
            setRiders([]);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenMap = (rider) => {
        setMapData(rider);
        setMapModalShow(true);
    };

    const handleViewOrder = (rider) => {
        if (rider.riderId) {
            history.push('/web_app/pnd_order/rider-wise-order/view/' + rider.riderId);
        }
    };

    const getStatusChip = (statusId, statusText) => {
        let color = 'default';
        if (statusId === 3 || statusId === 9) color = 'success';
        else if (statusId === 6) color = 'primary';
        else color = 'error';

        return <Chip label={statusText} color={color} size="small" sx={{ fontWeight: 'bold' }} />;
    };

    const getDutyChip = (rider) => {
        const { riderDutyStatusId, riderRoasterStatusName } = rider;
        let color = 'warning';
        if (riderDutyStatusId && (riderDutyStatusId === 2 || riderDutyStatusId === 6)) {
            color = 'success';
        }
        return <Chip label={riderRoasterStatusName || 'N/A'} color={color} size="small" variant="outlined" sx={{ fontWeight: 'bold', minWidth: '80px' }} />;
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
                <CircularProgress sx={{ color: '#32CA73' }} />
            </Box>
        );
    }

    const filteredRiders = riders.filter(rider => {
        const matchesStatus = statusFilter === 'all' || Number(rider.riderStatusId) === Number(statusFilter);
        const matchesRider = riderFilter === 'all' || Number(rider.riderId) === Number(riderFilter);
        return matchesStatus && matchesRider;
    });

    const paginatedRiders = filteredRiders.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Reset pagination when filter changes
    const handleStatusFilterChange = (e) => {
        setStatusFilter(e.target.value);
        setPage(0);
    };

    const handleRiderFilterChange = (e) => {
        setRiderFilter(e.target.value);
        setPage(0);
    };

    const uniqueStatuses = Array.from(new Set(riders.map(r => JSON.stringify({ id: r.riderStatusId, name: r.riderStatus }))))
        .map(s => JSON.parse(s))
        .filter(s => s.id && s.name);

    const uniqueRiders = Array.from(new Set(riders.map(r => JSON.stringify({ id: r.riderId, name: r.riderName }))))
        .map(s => JSON.parse(s))
        .filter(s => s.id && s.name)
        .sort((a, b) => a.name.localeCompare(b.name));

    return (
        <Box sx={{ width: '100%' }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Tabs
                    value={viewMode}
                    onChange={(e, v) => setViewMode(v)}
                    sx={{
                        bgcolor: '#f1f5f9',
                        borderRadius: 2,
                        p: 0.5,
                        '& .MuiTabs-indicator': { bgcolor: '#32CA73', borderRadius: 1.5, height: '100%', zIndex: 0 },
                        '& .MuiTab-root': { zIndex: 1, minHeight: 40, py: 1, px: 3, borderRadius: 1.5, transition: 'color 0.2s', '&.Mui-selected': { color: 'white' } }
                    }}
                >
                    <Tab value="grid" icon={<GridViewIcon fontSize="small" />} iconPosition="start" label="Grid View" />
                    <Tab value="map" icon={<MapIcon fontSize="small" />} iconPosition="start" label="Map View" />
                </Tabs>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Rider Status</InputLabel>
                        <Select
                            value={statusFilter}
                            label="Rider Status"
                            onChange={handleStatusFilterChange}
                            sx={{ borderRadius: 2, bgcolor: 'white' }}
                        >
                            <MenuItem value="all">All Statuses</MenuItem>
                            {uniqueStatuses.map(status => (
                                <MenuItem key={status.id} value={status.id}>{status.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Rider</InputLabel>
                        <Select
                            value={riderFilter}
                            label="Rider"
                            onChange={handleRiderFilterChange}
                            sx={{ borderRadius: 2, bgcolor: 'white' }}
                        >
                            <MenuItem value="all">All Riders</MenuItem>
                            {uniqueRiders.map(rider => (
                                <MenuItem key={rider.id} value={rider.id}>{rider.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Stack>
            </Stack>

            {viewMode === 'map' ? (
                <DashboardLiveMap riders={filteredRiders} />
            ) : (
                <TableContainer component={Card} sx={{ boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                    <Table sx={{ minWidth: 650 }} size="small" aria-label="modern rider table">
                        <TableHead sx={{ backgroundColor: '#f8fafc' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', color: '#64748b', borderBottom: '2px solid #e2e8f0', py: 1 }}>HUB</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', color: '#64748b', borderBottom: '2px solid #e2e8f0', py: 1 }}>RIDER</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', color: '#64748b', borderBottom: '2px solid #e2e8f0', py: 1 }}>STATUS</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', color: '#64748b', borderBottom: '2px solid #e2e8f0', py: 1 }}>DUTY</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold', color: '#64748b', borderBottom: '2px solid #e2e8f0', py: 1 }}>PENDING</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', color: '#64748b', borderBottom: '2px solid #e2e8f0', py: 1 }}>VEHICLE</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', color: '#64748b', borderBottom: '2px solid #e2e8f0', py: 1 }}>CONTACT</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold', color: '#64748b', borderBottom: '2px solid #e2e8f0', py: 1 }}>ACTIONS</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedRiders && paginatedRiders.length > 0 ? paginatedRiders.map((rider, index) => (
                                <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 }, '&:hover': { backgroundColor: '#f1f5f9' }, transition: 'background-color 0.2s' }}>
                                    <TableCell sx={{ py: 0.5 }}>{rider.storeName || 'N/A'}</TableCell>
                                    <TableCell sx={{ py: 0.5 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Avatar sx={{ bgcolor: '#32CA73', mr: 2, width: 28, height: 28, fontSize: '0.8rem', fontWeight: 'bold' }}>
                                                {rider.riderName ? rider.riderName[0].toUpperCase() : 'R'}
                                            </Avatar>
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#1e293b', fontSize: '12px' }}>
                                                    {rider.riderName || 'N/A'}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '10px' }}>
                                                    {rider.riderUserName || 'N/A'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ py: 0.5 }}>
                                        {getStatusChip(rider.riderStatusId, rider.riderStatus)}
                                    </TableCell>
                                    <TableCell sx={{ py: 0.5 }}>
                                        {getDutyChip(rider)}
                                    </TableCell>
                                    <TableCell align="center" sx={{ py: 0.5 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: rider.orderCount > 0 ? '#ef4444' : '#64748b', fontSize: '12px' }}>
                                            {rider.orderCount || 0}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ py: 0.5 }}>
                                        <Typography variant="body2" sx={{ color: '#334155', fontWeight: '500', fontSize: '12px' }}>{rider.plateNo || 'N/A'}</Typography>
                                        <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '10px' }}>
                                            {rider.vehicleMode || 'Unknown'} • {rider.speed || 0} km/h
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ py: 0.5 }}>
                                        <Typography variant="body2" sx={{ color: '#334155', fontWeight: '500', fontSize: '12px' }}>
                                            {rider.mobileNo || 'N/A'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center" sx={{ py: 0.5 }}>
                                        <Tooltip title="View Order Details">
                                            <IconButton size="small" onClick={() => handleViewOrder(rider)} sx={{ color: '#3b82f6', mr: 1, backgroundColor: '#eff6ff', '&:hover': { backgroundColor: '#dbeafe' }, p: 0.5 }}>
                                                <InfoIcon sx={{ fontSize: 18 }} />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="View Live Location">
                                            <IconButton size="small" onClick={() => handleOpenMap(rider)} sx={{ color: '#10b981', backgroundColor: '#ecfdf5', '&:hover': { backgroundColor: '#d1fae5' }, p: 0.5 }}>
                                                <MapIcon sx={{ fontSize: 18 }} />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            )) : (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 6, color: '#94a3b8' }}>
                                        <Typography variant="body1">No riders match the selected status.</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={filteredRiders.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        sx={{ borderTop: '1px solid #e2e8f0' }}
                    />
                </TableContainer>
            )}

            {mapModalShow && (
                <RiderLocationMapModal
                    modalShow={mapModalShow}
                    setModalShow={setMapModalShow}
                    data={mapData}
                />
            )}
        </Box>
    );
};

export default LiveMapRiderGrid;
